/**
 * Queen B Academy - Course Data
 * Complete course content for CDL Mastery 101, Dispatcher 101, Trucking 101, and Freight Broker 101
 */

export const ACADEMY_INFO = {
  name: "Queen B Academy",
  tagline: "Education Built for the Road Ahead",
  description: "Premium trucking education designed to transform beginners into industry professionals. Our comprehensive courses combine real-world expertise with actionable strategies.",
  pricing: {
    individual: 250,
    bundle: 800,
    bundleSavings: 200
  },
  accessCodePrefix: "QBA"
};

export const COURSES = [
  {
    id: "cdl-mastery-101",
    title: "CDL Mastery 101",
    subtitle: "Complete Commercial Driver Study Guide",
    description: "A full CDL preparation system designed to help users pass their permit and road test. Master everything from general knowledge to pre-trip inspections with our comprehensive study guide.",
    price: 250,
    duration: "8 weeks",
    level: "Beginner",
    category: "Licensing",
    icon: "Truck",
    color: "#D4AF37",
    features: [
      "Practice quizzes per module",
      "Printable study sheets",
      "Pre-trip checklist download",
      "Clean study guide format",
      "DOT & Safety requirements coverage",
      "Road test preparation tips"
    ],
    outcomes: [
      "Pass your CDL written exam on the first attempt",
      "Execute a flawless pre-trip inspection",
      "Understand air brake systems completely",
      "Master backing skills and maneuvers",
      "Know your DOT & safety requirements"
    ],
    modules: [
      {
        id: "cdl-m1",
        title: "CDL Foundations",
        description: "Understanding CDL classes, endorsements, and requirements",
        lessons: [
          {
            id: "cdl-m1-l1",
            title: "CDL Classes (A, B, C)",
            duration: "30 min",
            content: `Understanding the three CDL classes is fundamental to your commercial driving career.

**Class A CDL:**
- Allows operation of combination vehicles with GCWR of 26,001+ lbs
- Towed vehicle must weigh more than 10,000 lbs
- Most versatile license - covers tractor-trailers, tankers, flatbeds
- Required for long-haul trucking careers

**Class B CDL:**
- Single vehicles with GVWR of 26,001+ lbs
- Can tow vehicles under 10,000 lbs
- Common for: straight trucks, large buses, dump trucks
- Good for local delivery and transit careers

**Class C CDL:**
- Vehicles designed for 16+ passengers (including driver)
- Vehicles transporting hazardous materials
- Common for: small buses, hazmat vehicles
- Entry point for many commercial driving careers

**Choosing Your Class:**
- Career goals determine your best choice
- Class A opens the most opportunities
- Many employers require Class A even for Class B work
- Consider endorsements needed for your target industry`
          },
          {
            id: "cdl-m1-l2",
            title: "Endorsements Overview",
            duration: "25 min",
            content: `Endorsements expand what you can legally transport with your CDL.

**H - Hazardous Materials:**
- Required for placarded hazmat loads
- Requires TSA background check
- Written test + security clearance
- Renew every 5 years

**N - Tank Vehicles:**
- For liquid bulk tanks
- Covers tanker dynamics and safety
- Often combined with H for "X" endorsement

**P - Passenger:**
- Required for vehicles carrying 16+ passengers
- Covers passenger safety and emergency procedures
- Pre-trip inspection emphasis on passenger safety

**S - School Bus:**
- Requires P endorsement first
- Additional background checks
- Specific loading/unloading procedures
- State-specific requirements vary

**T - Double/Triple Trailers:**
- For pulling multiple trailers
- Advanced coupling/uncoupling
- Additional handling considerations

**X - Combination Tank/Hazmat:**
- Combines H and N endorsements
- Maximum earning potential
- Most stringent requirements`
          },
          {
            id: "cdl-m1-l3",
            title: "State vs Federal Requirements",
            duration: "20 min",
            content: `Understanding the relationship between state and federal CDL requirements.

**Federal Standards (FMCSA):**
- Sets minimum standards nationwide
- CDL testing requirements
- Medical qualifications
- Hours of service rules
- Drug and alcohol testing
- Entry Level Driver Training (ELDT)

**State Administration:**
- Each state issues CDLs
- Can exceed federal minimums
- Handles testing and licensing
- Sets fees and renewal periods
- Manages driving records

**Key Federal Requirements:**
- Must be 21 for interstate commerce
- 18-20 can drive intrastate (state dependent)
- Valid medical certificate required
- Clean driving record standards
- English language proficiency

**State Variations:**
- Testing locations and procedures
- Fee structures
- Renewal periods
- Additional state endorsements
- Reciprocity agreements

**Your Responsibilities:**
- Know your state's specific rules
- Maintain current documentation
- Report violations to employer
- Keep medical certificate current`
          }
        ]
      },
      {
        id: "cdl-m2",
        title: "General Knowledge",
        description: "Road rules, safe driving practices, and hazard awareness",
        lessons: [
          {
            id: "cdl-m2-l1",
            title: "Road Rules",
            duration: "35 min",
            content: `Commercial drivers must master road rules for safe operation.

**Speed Management:**
- Posted limits are for ideal conditions
- Reduce speed for weather, traffic, road conditions
- Stopping distance increases with speed and weight
- Know when to slow down before curves and exits

**Right-of-Way Rules:**
- Yield signs require slowing and yielding
- Stop signs require complete stops
- Roundabouts: yield to traffic in circle
- Emergency vehicles: pull right and stop

**Lane Usage:**
- Stay in right lane except when passing
- Use turn lanes appropriately
- Merge smoothly with traffic flow
- Signal lane changes 100+ feet ahead

**Traffic Signals:**
- Yellow means stop if safe
- Right on red: stop first, yield to traffic
- Flashing signals: treat as stop/yield
- Malfunctioning signals: treat as 4-way stop

**Railroad Crossings:**
- Never race a train
- Required stops for certain vehicles
- Never shift gears on tracks
- Expect trains on any track at any time`
          },
          {
            id: "cdl-m2-l2",
            title: "Safe Driving Practices",
            duration: "40 min",
            content: `Professional drivers prioritize safety above all else.

**Following Distance:**
- Minimum 1 second per 10 feet of vehicle length
- At 40+ mph, add 1 additional second
- Double distance in adverse conditions
- Leave space for escape routes

**Mirror Usage:**
- Check mirrors every 5-8 seconds
- Know your blind spots
- Adjust before starting trip
- Clean mirrors regularly

**Scanning Ahead:**
- Look 12-15 seconds ahead
- Identify hazards early
- Watch for brake lights ahead
- Anticipate traffic flow changes

**Night Driving:**
- Reduce speed - vision limited
- Use high beams when appropriate
- Keep windshield and lights clean
- Watch for drowsy driving signs

**Fatigue Management:**
- Know your limits
- Take breaks every 2-3 hours
- Don't rely on caffeine alone
- Pull over if drowsy - don't push through`
          },
          {
            id: "cdl-m2-l3",
            title: "Vehicle Control",
            duration: "35 min",
            content: `Controlling a commercial vehicle requires specific techniques.

**Acceleration:**
- Smooth, gradual acceleration
- Don't spin wheels on wet surfaces
- Match speed to traffic flow
- Watch for wheel spin indicators

**Braking:**
- Apply brakes smoothly and early
- Don't brake and turn simultaneously
- Know your stopping distance
- Use engine braking on downgrades

**Steering:**
- Both hands on wheel
- Small, smooth corrections
- Don't oversteer
- Watch for road crown effects

**Backing:**
- GOAL: Get Out And Look
- Use spotter when available
- Correct immediately if off-path
- Pull forward to reposition if needed

**Turning:**
- Turn wide for right turns
- Know your off-tracking
- Check mirrors continuously
- Complete turn in proper lane`
          },
          {
            id: "cdl-m2-l4",
            title: "Hazard Awareness",
            duration: "30 min",
            content: `Recognizing and responding to hazards keeps you safe.

**Road Hazards:**
- Work zones: slow down, follow signs
- Uneven pavement: reduce speed
- Debris: avoid if safe, report large items
- Weather damage: report serious issues

**Other Drivers:**
- Expect the unexpected
- Watch for distracted drivers
- Give aggressive drivers space
- Protect yourself from road rage

**Pedestrians and Cyclists:**
- Extra caution in urban areas
- Watch near schools and parks
- Assume they don't see you
- Yield even when you have right-of-way

**Weather Hazards:**
- Rain: reduce speed, increase following
- Fog: use low beams, slow significantly
- Ice: gentle inputs, test traction
- Wind: watch for trailer sway

**Emergency Response:**
- Know emergency procedures
- Stay calm under pressure
- Communicate clearly
- Document incidents thoroughly`
          }
        ]
      },
      {
        id: "cdl-m3",
        title: "Air Brakes",
        description: "Air brake system components, inspection, and failure warnings",
        lessons: [
          {
            id: "cdl-m3-l1",
            title: "Air Brake System Components",
            duration: "45 min",
            content: `Understanding air brake components is essential for safe operation.

**Air Compressor:**
- Pumps air into storage tanks
- Usually gear or belt-driven from engine
- Builds pressure to 125+ PSI
- Must build from 85-100 PSI in 45 seconds

**Governor:**
- Controls compressor operation
- Cut-out: ~125 PSI (stops pumping)
- Cut-in: ~100 PSI (starts pumping)
- Automatic regulation

**Air Storage Tanks:**
- Hold compressed air
- Multiple tanks for safety
- Must be drained daily
- First tank is "wet" tank (most moisture)

**Brake Chambers:**
- Convert air pressure to mechanical force
- Push rods extend when air applied
- Standard and long-stroke types
- Check for damage during inspection

**Slack Adjusters:**
- Connect chambers to brake drums
- Manual or automatic types
- Check adjustment during pre-trip
- Critical for proper braking

**Brake Drums and Linings:**
- Where friction creates stopping power
- Check for cracks and wear
- Heat is the enemy
- Listen for grinding sounds`
          },
          {
            id: "cdl-m3-l2",
            title: "Inspection Procedures",
            duration: "40 min",
            content: `Proper inspection of air brakes is a critical safety requirement.

**Visual Inspection:**
- Check brake drums/discs for cracks
- Look for worn or missing parts
- Inspect air lines for damage
- Check chamber mounts
- Verify slack adjuster condition

**Operational Tests:**

*Air Leakage Test:*
1. Build to full pressure
2. Turn off engine
3. Release parking brake
4. Apply service brake
5. Hold for 1 minute
6. Should not leak more than 3 PSI (single) or 4 PSI (combination)

*Low Pressure Warning Test:*
1. With engine off, pump brake pedal
2. Warning must activate before 60 PSI
3. Both audible and visual warning
4. Critical safety feature

*Spring Brake Test:*
1. Continue pumping after warning
2. Parking brake should pop out at 20-45 PSI
3. Automatic protection feature
4. Prevents driving with low air

**Brake Adjustment Check:**
- Park on level ground
- Chock wheels
- Check slack adjuster angle
- Should be less than 90 degrees
- Adjustment critical for stopping power`
          },
          {
            id: "cdl-m3-l3",
            title: "Failure Warnings",
            duration: "30 min",
            content: `Recognizing brake problems before they become emergencies.

**Warning Signs:**

*Low Air Pressure Warning:*
- Activates below 60 PSI
- Both light and buzzer
- STOP immediately if it activates while driving
- Don't continue until problem fixed

*Air Pressure Drop:*
- Watch gauges constantly
- Pressure should build, not drop
- Dropping pressure = serious problem
- Pull over safely, investigate

*Unusual Sounds:*
- Grinding = worn linings
- Squealing = glazed linings
- Air leaking = damaged lines
- Popping = potential chamber issue

**Emergency Procedures:**

*Total Air Loss:*
- Spring brakes will apply automatically
- Vehicle will stop (not gently)
- Stay calm, steer straight
- Get off road if possible

*Partial Failure:*
- Use remaining brakes carefully
- Reduce speed immediately
- Plan stop far ahead
- Get to safe location for repair

**Prevention:**
- Daily pre-trip inspection
- Listen for leaks
- Watch pressure gauges
- Drain tanks daily
- Report problems immediately`
          }
        ]
      },
      {
        id: "cdl-m4",
        title: "Combination Vehicles",
        description: "Coupling, uncoupling, trailer dynamics, and rollover prevention",
        lessons: [
          {
            id: "cdl-m4-l1",
            title: "Coupling & Uncoupling",
            duration: "45 min",
            content: `Proper coupling and uncoupling is essential for combination vehicle safety.

**Coupling Procedure:**

*Step 1: Inspect Fifth Wheel*
- Check for damage and proper lubrication
- Ensure jaws are open
- Tilt plate should angle toward rear
- Release handle in automatic lock position

*Step 2: Inspect Trailer*
- Kingpin not damaged
- Landing gear fully raised
- Air and electrical connections accessible

*Step 3: Position Tractor*
- Back slowly under trailer
- Stop when fifth wheel contacts trailer
- Check alignment before engaging

*Step 4: Secure Connection*
- Back until kingpin locks
- Pull forward gently to test
- Get out and verify visually
- Jaws closed around kingpin
- No space between surfaces
- Release handle locked

*Step 5: Connect Lines*
- Connect air lines (color coded)
- Connect electrical cord
- Check connections are secure

*Step 6: Final Checks*
- Raise landing gear completely
- Check trailer brakes
- Test all lights

**Uncoupling Procedure:**
1. Position on firm, level ground
2. Secure trailer (parking brakes)
3. Lower landing gear
4. Disconnect lines
5. Release fifth wheel lock
6. Pull tractor forward slowly
7. Verify complete separation`
          },
          {
            id: "cdl-m4-l2",
            title: "Trailer Dynamics",
            duration: "35 min",
            content: `Understanding how trailers behave is critical for safe operation.

**Off-Tracking:**
- Trailer wheels follow shorter path than tractor
- Worse in turns and curves
- Plan for it when turning
- Watch inside mirrors for clearance

**Rearward Amplification (Crack-the-Whip):**
- Sudden movements amplify toward rear
- Worse with multiple trailers
- Smooth steering inputs essential
- Speed management critical

**Trailer Swing:**
- Rear of trailer swings opposite direction in turns
- Can strike objects or other vehicles
- Allow extra clearance when turning
- Watch mirrors continuously

**Weight Distribution Effects:**

*Nose-Heavy Trailer:*
- Poor steering response
- Front tires overloaded
- Check weight distribution

*Tail-Heavy Trailer:*
- Light steering, reduced traction
- Potential trailer swing issues
- Rear axle overloaded

**Speed Effects:**
- All handling problems worse at speed
- Reduce speed in curves
- Anticipate handling changes
- Allow extra stopping distance`
          },
          {
            id: "cdl-m4-l3",
            title: "Rollover Prevention",
            duration: "30 min",
            content: `Rollovers are among the most serious truck accidents. Prevention is key.

**Rollover Causes:**

*Excessive Speed:*
- #1 cause of rollovers
- Curves and ramps especially dangerous
- Posted limits for cars, not trucks
- Slow down significantly

*Sudden Maneuvers:*
- Abrupt steering causes shift
- Quick lane changes dangerous
- Emergency swerves risky
- Plan ahead to avoid sudden moves

*Load Shifts:*
- Improperly secured loads move
- Liquids slosh in tankers
- Top-heavy loads unstable
- Verify load security

**High-Risk Situations:**

*Freeway Ramps:*
- Posted speeds too fast for trucks
- Reduce speed before entering
- Watch for decreasing radius curves
- Don't brake hard in curve

*Curves:*
- Slow down before curve
- Don't brake in curve
- Watch for road camber
- Load height affects stability

**Prevention Techniques:**
- Know your vehicle's limits
- Always slow for curves
- Secure loads properly
- Smooth steering inputs
- Leave escape routes
- When in doubt, slow down`
          }
        ]
      },
      {
        id: "cdl-m5",
        title: "Pre-Trip Inspection",
        description: "Step-by-step inspection walkthrough for your CDL test",
        lessons: [
          {
            id: "cdl-m5-l1",
            title: "Step-by-Step Inspection Walkthrough",
            duration: "30 min",
            content: `A systematic approach ensures you don't miss anything.

**Overview:**
- Start at same point each time
- Work methodically around vehicle
- Verbalize what you're checking (for test)
- Look for damage, leaks, security

**Seven-Point Inspection Method:**
1. Front of vehicle
2. Driver side
3. Rear of tractor
4. Coupling area
5. Rear of trailer
6. Passenger side
7. Return to cab

**What Examiners Look For:**
- Systematic approach
- Knowledge of components
- Correct terminology
- Understanding of "why" not just "what"
- Confidence and professionalism

**Time Management:**
- Full inspection: 30-45 minutes
- Test inspection: 10-15 minutes
- Practice until smooth
- Don't rush, don't drag

**Common Test Failures:**
- Missing components
- Incorrect terminology
- Unsystematic approach
- Not explaining "why"
- Nervousness affecting performance`
          },
          {
            id: "cdl-m5-l2",
            title: "Engine Compartment",
            duration: "35 min",
            content: `The engine compartment inspection covers critical systems.

**Before Opening Hood:**
- Set parking brake
- Wheels chocked
- Engine off and cooled

**Fluid Levels:**

*Engine Oil:*
- Locate dipstick
- Check level between marks
- Look for contamination
- Check for leaks below

*Coolant:*
- Check overflow reservoir
- Never open hot radiator cap
- Look for proper level
- Check for leaks

*Power Steering:*
- Locate reservoir
- Check level indicator
- Fluid should be clean

**Belts and Hoses:**
- Check belt tension
- Look for cracks, fraying
- Hoses shouldn't be bulging
- All connections secure

**Other Checks:**
- Battery connections tight
- Air compressor mounting
- Alternator belt
- Wiring not chafed
- No fluid leaks anywhere`
          },
          {
            id: "cdl-m5-l3",
            title: "In-Cab Checks",
            duration: "30 min",
            content: `In-cab inspection ensures all controls and gauges work properly.

**Gauges:**
- Oil pressure (should rise quickly)
- Coolant temperature (normal range)
- Ammeter/voltmeter (charging)
- Air pressure (builds properly)
- All warning lights function

**Controls:**
- Steering wheel play (max 2")
- Horn works
- Windshield wipers all speeds
- Heater/defroster works
- All mirrors adjustable

**Safety Equipment:**
- Fire extinguisher charged
- Warning triangles (3 required)
- Spare fuses (if not breakers)
- All required paperwork present

**Brake Checks:**
- Parking brake holds
- Service brake response
- Air builds properly
- Warning system works

**Other Checks:**
- Seats secure and adjustable
- Seatbelt works
- All required lights work
- Emergency equipment accessible
- Cab clean and organized`
          },
          {
            id: "cdl-m5-l4",
            title: "Trailer Inspection",
            duration: "35 min",
            content: `Trailer inspection ensures safe cargo transport.

**Coupling Area:**
- Fifth wheel secure
- Kingpin locked properly
- No gap between surfaces
- Air lines connected properly
- Electrical connection secure
- Landing gear fully raised
- Platform clear of debris

**Trailer Body:**
- Front area secure
- Sides no damage
- Doors secure
- Roof intact (if visible)
- Proper markings visible
- No leaks from cargo

**Wheels and Tires:**
- Proper inflation
- Tread depth adequate
- No damage or objects
- Lug nuts tight
- Hub seals not leaking
- Brake components visible

**Rear:**
- All lights work
- Reflectors present
- License plate visible
- Doors/latches secure
- ICC bar secure
- Mud flaps present`
          }
        ]
      },
      {
        id: "cdl-m6",
        title: "Backing Skills",
        description: "Straight line backing, offset backing, and parallel parking",
        lessons: [
          {
            id: "cdl-m6-l1",
            title: "Straight Line Backing",
            duration: "30 min",
            content: `Straight line backing is foundational for all backing maneuvers.

**Basic Principles:**
- Small steering corrections early
- Trailer goes opposite direction of steering
- Use both mirrors constantly
- Go slowly - speed is your enemy

**Technique:**
1. Position vehicle straight
2. Put in reverse
3. Release brakes slowly
4. Watch both mirrors equally
5. Correct early and small
6. Stop if unsure

**Mirror Usage:**
- Equal attention to both
- Whichever side moves away from line
- Steer toward that side (top of wheel)
- Straighten quickly after correction

**Common Mistakes:**
- Over-correcting
- Looking at one mirror only
- Going too fast
- Waiting too long to correct
- Not GOAL-ing when unsure

**Practice Tips:**
- Start on open lot
- Use cones or markers
- Repeat until consistent
- Build muscle memory
- Stay calm and patient`
          },
          {
            id: "cdl-m6-l2",
            title: "Offset Backing",
            duration: "35 min",
            content: `Offset backing requires moving into a space beside your starting position.

**Setup:**
- Position 12-15 feet from target space
- Vehicle parallel to boundary
- Know which direction offset is

**Left Offset (sight side):**
1. Turn wheel hard left
2. Back slowly watching left mirror
3. When trailer enters space, straighten
4. Continue straight into space
5. Final corrections as needed

**Right Offset (blind side):**
1. Turn wheel hard right
2. Back slowly watching right mirror
3. Use GOAL frequently
4. When trailer enters space, straighten
5. More difficult - take your time

**Reference Points:**
- When door handle passes marker, turn
- Practice finding your points
- Every truck/trailer different
- Consistency comes with practice

**Test Tips:**
- Usually get one correction
- Pull forward if badly positioned
- Don't hit boundaries
- Stay calm
- GOAL saves points`
          },
          {
            id: "cdl-m6-l3",
            title: "Parallel Parking Concepts",
            duration: "30 min",
            content: `Parallel parking a commercial vehicle requires planning and patience.

**Conventional (Blind Side) Parking:**
- More difficult
- Parking spot on right
- Limited visibility
- Use GOAL liberally

**Sight Side Parking:**
- Parking spot on left
- Better visibility
- Still requires practice
- Preferred when possible

**General Technique:**
1. Pull past space
2. Begin backing turn
3. Watch for trailer to enter
4. Straighten as needed
5. Final positioning

**Space Requirements:**
- Need more space than car
- Trailer tracks inside
- Account for off-tracking
- Don't force tight fits

**Key Points:**
- Go slowly
- GOAL often
- Small corrections
- Know your references
- Practice, practice, practice

**Test Day:**
- Boundaries marked clearly
- Usually have some tolerance
- Encroachments cost points
- Calm confidence wins`
          }
        ]
      },
      {
        id: "cdl-m7",
        title: "Road Test Preparation",
        description: "What examiners look for and common mistakes to avoid",
        lessons: [
          {
            id: "cdl-m7-l1",
            title: "What Examiners Look For",
            duration: "30 min",
            content: `Understanding the examiner's perspective helps you prepare.

**Overall Assessment:**
- Safe vehicle operation
- Knowledge of vehicle
- Following traffic laws
- Professional conduct
- Calm under pressure

**Pre-Trip Component:**
- Systematic approach
- Correct identification
- Proper terminology
- Understanding purpose
- Nothing missed

**Basic Control Skills:**
- Smooth operation
- Accurate maneuvering
- Proper corrections
- Staying in boundaries
- Appropriate speed

**Road Driving:**
- Mirror usage
- Speed management
- Lane position
- Traffic interaction
- Signal usage
- Smooth braking
- Proper turns

**Automatic Failures:**
- Accident
- Traffic violation
- Dangerous action
- Examiner intervention
- Refusing examiner instruction`
          },
          {
            id: "cdl-m7-l2",
            title: "Common Mistakes",
            duration: "25 min",
            content: `Knowing common mistakes helps you avoid them.

**Pre-Trip Mistakes:**
- Skipping components
- Wrong terminology
- Rushing through
- Not explaining "why"
- Missing obvious issues

**Basic Control Mistakes:**
- Going too fast
- Over-correcting
- Not using GOAL
- Hitting boundaries
- Giving up too early

**Road Test Mistakes:**
- Forgetting signals
- Rolling stops
- Improper lane changes
- Following too close
- Speed violations
- Wide/tight turns
- Poor mirror usage

**Mental Mistakes:**
- Nervousness affecting performance
- Rushing
- Not asking for clarification
- Arguing with examiner
- Giving up after error

**Physical Mistakes:**
- Improper hand position
- Not wearing seatbelt
- Improper mirror adjustment
- Starting before ready
- Improper entry/exit of cab`
          },
          {
            id: "cdl-m7-l3",
            title: "Confidence Tips",
            duration: "25 min",
            content: `Mental preparation is as important as physical skills.

**Before Test Day:**
- Practice until consistent
- Know the test route if possible
- Get good sleep
- Eat well, stay hydrated
- Arrive early

**Managing Nerves:**
- Deep breathing helps
- Positive self-talk
- Visualize success
- Remember your training
- It's okay to be nervous

**During the Test:**
- Ask questions if unclear
- Take your time
- Breathe steadily
- Focus on current task
- Don't dwell on mistakes

**Recovery from Errors:**
- Everyone makes mistakes
- One error rarely fails you
- Refocus immediately
- Complete the test
- Stay professional

**Confidence Builders:**
- Know you've practiced
- Trust your training
- Remember others pass daily
- You've got this
- Professional attitude wins`
          }
        ]
      },
      {
        id: "cdl-m8",
        title: "DOT & Safety Requirements",
        description: "Drug testing, Clearinghouse, and ELDT requirements (2026)",
        lessons: [
          {
            id: "cdl-m8-l1",
            title: "Drug Testing",
            duration: "30 min",
            content: `DOT drug testing is mandatory for all CDL holders.

**When Testing Occurs:**
- Pre-employment
- Random (unannounced)
- Post-accident
- Reasonable suspicion
- Return-to-duty
- Follow-up

**Substances Tested:**
- Marijuana
- Cocaine
- Opiates
- Amphetamines
- PCP

**Testing Process:**
- Urine collection
- Chain of custody
- Lab analysis
- Medical Review Officer
- Results to employer

**Consequences of Failure:**
- Immediate removal from duty
- Reported to Clearinghouse
- SAP evaluation required
- Treatment/education
- Return-to-duty test
- Follow-up testing

**Your Rights:**
- Request split specimen test
- Medical Review Officer review
- Legal prescription verification
- Confidentiality (with limits)

**Your Responsibilities:**
- Disclose prescriptions
- Don't refuse tests
- Know what you consume
- Stay clean`
          },
          {
            id: "cdl-m8-l2",
            title: "Clearinghouse",
            duration: "25 min",
            content: `The FMCSA Drug & Alcohol Clearinghouse tracks violations.

**What is the Clearinghouse?**
- Federal database
- Tracks drug/alcohol violations
- Employers must query before hire
- Annual queries for current employees

**What Gets Reported:**
- Positive drug tests
- Alcohol violations
- Refusals to test
- Return-to-duty status
- Follow-up testing info

**How It Affects You:**
- Violations follow you
- Any employer can see
- Must resolve before driving
- Stays on record 5 years

**Your Access:**
- Can view your record
- Can see who queried
- Can dispute errors
- Register at clearinghouse.fmcsa.dot.gov

**Employer Obligations:**
- Pre-employment query
- Annual query
- Report violations within 3 days
- Use FMCSA-approved labs

**Staying Clean:**
- Register in system
- Know your record
- Address violations properly
- Don't try to hide history`
          },
          {
            id: "cdl-m8-l3",
            title: "ELDT Requirements (2026)",
            duration: "30 min",
            content: `Entry Level Driver Training requirements are mandatory for new CDL applicants.

**Who Needs ELDT:**
- New CDL applicants
- CDL upgrade applicants
- Endorsement additions (H, P, S)
- Since February 7, 2022

**Exemptions:**
- Military CDL holders
- Certain pre-rule CDL holders
- Some endorsement restrictions

**Training Requirements:**

*Theory (Classroom):*
- Varies by class and endorsement
- FMCSA-approved curriculum
- Must pass knowledge assessment

*Behind-the-Wheel (BTW):*
- Range training required
- Road training required
- No minimum hours specified
- Must demonstrate proficiency

**Training Provider Requirements:**
- Must be registered with FMCSA
- On Training Provider Registry (TPR)
- Meet curriculum standards
- Report completion to FMCSA

**Your Responsibilities:**
- Choose registered provider
- Complete all required training
- Verify completion reported
- Then take CDL test

**Verifying Providers:**
- Check TPR at FMCSA website
- Verify current registration
- Check reviews/reputation
- Avoid unregistered "schools"

**2026 Updates:**
- Enhanced curriculum standards
- Stricter provider oversight
- Additional safety modules
- Check current requirements`
          }
        ]
      }
    ]
  },
  {
    id: "dispatcher-101",
    title: "Dispatcher 101",
    subtitle: "Freight Dispatch Fundamentals",
    description: "Learn how to operate as a professional dispatcher and manage freight efficiently. Master load boards, rate negotiation, and carrier management.",
    price: 250,
    duration: "6 weeks",
    level: "Beginner to Intermediate",
    category: "Dispatch",
    icon: "Headphones",
    color: "#C0C0C0",
    features: [
      "Call scripts included",
      "Email templates",
      "Dispatch checklist downloads",
      "Workflow visual system",
      "Rate negotiation training",
      "Invoicing & payment guidance"
    ],
    outcomes: [
      "Navigate major load boards effectively",
      "Negotiate profitable rates for carriers",
      "Build lasting broker relationships",
      "Create efficient dispatch workflows",
      "Understand invoicing and factoring",
      "Maximize revenue per mile"
    ],
    modules: [
      {
        id: "disp-m1",
        title: "Dispatcher Role & Responsibilities",
        description: "Understanding the daily workflow and communication expectations",
        lessons: [
          {
            id: "disp-m1-l1",
            title: "Daily Workflow",
            duration: "35 min",
            content: `Understanding the dispatcher's daily routine sets you up for success.

**Morning (5-8 AM):**
- Check driver status and location
- Review loads picked up overnight
- Identify trucks needing loads
- Check load board postings
- Review deadhead situations

**Mid-Morning (8-11 AM):**
- Active load searching
- Rate negotiations
- Booking confirmed loads
- Sending rate confirmations
- Coordinating pickups

**Afternoon (11 AM - 3 PM):**
- Track in-transit loads
- Handle check calls
- Address pickup/delivery issues
- Continue booking for next day
- Document all communications

**Late Day (3-6 PM):**
- Confirm deliveries
- Plan next-day loads
- Update carrier on schedule
- Handle paperwork
- Review daily performance

**Evening (as needed):**
- Monitor late deliveries
- Handle emergencies
- Prepare for next day
- Available for driver issues

**Weekly Tasks:**
- Review carrier settlements
- Analyze lane performance
- Broker relationship maintenance
- Rate trend analysis`
          },
          {
            id: "disp-m1-l2",
            title: "Communication Expectations",
            duration: "30 min",
            content: `Professional communication is the foundation of dispatch success.

**With Drivers:**
- Clear, concise instructions
- Respect their time and knowledge
- Proactive updates
- Available for problems
- Document everything

**With Brokers:**
- Professional tone always
- Quick response times
- Accurate information
- Follow through on commitments
- Build relationships

**Communication Channels:**

*Phone:*
- Primary for urgent matters
- Keep calls focused
- Take good notes
- Return calls promptly

*Email:*
- Documentation trail
- Rate confirmations
- Non-urgent matters
- Professional formatting

*Text:*
- Quick updates
- Time-sensitive alerts
- Keep professional
- Don't rely on alone

**Response Time Standards:**
- Broker calls: within 15 minutes
- Driver calls: immediately when possible
- Emails: same day
- Load updates: as they happen

**Documentation:**
- Log all broker conversations
- Save email threads
- Note load numbers and details
- Track issues and resolutions`
          }
        ]
      },
      {
        id: "disp-m2",
        title: "Finding Loads",
        description: "Master load boards and broker sourcing strategies",
        lessons: [
          {
            id: "disp-m2-l1",
            title: "Load Boards (DAT, Truckstop)",
            duration: "40 min",
            content: `Load boards are your primary tool for finding freight.

**DAT Load Board:**
- Industry's largest network
- DAT Power, DAT One products
- Comprehensive rate data
- Credit score access
- Mobile app available

*Key Features:*
- Load search by lane
- Rate analytics
- Broker credit scores
- Truck posting capability
- Load alerts

**Truckstop.com:**
- Major competitor to DAT
- Similar functionality
- Integrated TMS options
- Book It Now feature
- Credit information

*Key Features:*
- Load matching
- Rate analysis
- Carrier onboarding tools
- Document management
- Payment services

**Using Load Boards Effectively:**

*Search Strategies:*
- Set radius appropriately
- Filter by equipment type
- Check multiple times daily
- Use alerts for key lanes

*Evaluating Loads:*
- Rate per mile
- Deadhead to pickup
- Delivery location (next load?)
- Broker reputation
- Special requirements

**Cost Considerations:**
- Subscriptions: $40-300/month
- ROI usually positive
- Compare features vs cost
- Consider multiple boards`
          },
          {
            id: "disp-m2-l2",
            title: "Broker Sourcing Strategies",
            duration: "35 min",
            content: `Beyond load boards, strategic broker relationships increase your options.

**Building Broker Relationships:**

*Initial Contact:*
- Professional introduction
- Carrier packet ready
- Ask about their lanes
- Express interest in consistent freight

*Qualification Process:*
- Check their MC authority
- Review credit rating
- Ask about payment terms
- Verify contact information

**Direct Outreach:**
- Research brokers in your lanes
- Cold call with professional pitch
- Follow up with email
- Send carrier packet promptly

**Types of Brokers:**

*Large Brokerages:*
- TQL, CH Robinson, Landstar
- High volume, many loads
- Standardized processes
- Reliable payment

*Small/Medium Brokers:*
- More personalized service
- Better rate flexibility
- Building relationships easier
- May have niche freight

**Maintaining Relationships:**
- Deliver consistently
- Communicate proactively
- Be honest about issues
- Ask for feedback
- Request more freight when delivering well

**Red Flags:**
- Very new authority
- No credit history
- Avoid payment questions
- Rates too good to be true
- Unprofessional communication`
          }
        ]
      },
      {
        id: "disp-m3",
        title: "Rate Negotiation",
        description: "Master negotiation scripts and market rate understanding",
        lessons: [
          {
            id: "disp-m3-l1",
            title: "Negotiation Scripts",
            duration: "40 min",
            content: `Effective negotiation follows proven patterns.

**Initial Call Script:**

"Hi, this is [Name] with [Company]. I'm calling about your load posting from [origin] to [destination]. Is that still available?"

*If yes:* "Great. What rate are you offering on that?"

**Counter-Offer Script:**

"I appreciate that rate, but we're looking at [your rate] for this lane. Our truck is [close/available/has good service record]."

**Justification Phrases:**
- "Based on current market rates..."
- "Considering fuel costs and the distance..."
- "For a driver with our service level..."
- "Given the pickup timing..."

**Closing Scripts:**

*When they accept:*
"Perfect. Can you send the rate confirmation to [email]? I'll have our driver there by [time]."

*When negotiating:*
"What's the best you can do? We want to work together on this."

*When walking away:*
"I understand. If anything changes, please give me a call. We'd like to work with you in the future."

**Key Principles:**
- Know your floor rate
- Stay professional always
- Don't bluff
- Build relationships, not just transactions
- Document agreed terms immediately`
          },
          {
            id: "disp-m3-l2",
            title: "Understanding Market Rates",
            duration: "35 min",
            content: `Knowing market rates gives you negotiation power.

**Rate Factors:**

*Distance:*
- Longer hauls = lower per-mile rate
- Short hauls = higher per-mile rate
- Minimum charges for very short

*Lane Demand:*
- High-demand lanes pay more
- Backhaul lanes pay less
- Seasonal variations significant

*Timing:*
- Last-minute loads pay premium
- Early booking = lower rates
- Day of week matters

*Equipment:*
- Flatbed > Reefer > Dry Van (typically)
- Specialized = premium rates
- Capacity affects pricing

**Rate Research Tools:**

*DAT RateView:*
- Historical rate data
- Lane-specific information
- Trend analysis

*Truckstop Rate Analysis:*
- Similar functionality
- Market comparisons

**Calculating Minimum Rate:**

Revenue needed = (Driver pay + Fuel + Fixed costs + Profit) ÷ Loaded miles

Example:
- Driver: $0.50/mile
- Fuel: $0.45/mile
- Fixed costs: $0.20/mile
- Profit: $0.15/mile
- Minimum: $1.30/mile loaded

**Don't Forget:**
- Deadhead miles count
- Detention possibility
- Lumper fees
- Tolls on route`
          }
        ]
      },
      {
        id: "disp-m4",
        title: "Broker Communication",
        description: "Professional call structure and email templates",
        lessons: [
          {
            id: "disp-m4-l1",
            title: "Call Structure",
            duration: "30 min",
            content: `Structured calls maximize your effectiveness.

**Pre-Call Preparation:**
- Know the load details
- Have rate range in mind
- Know your driver's status
- Have carrier packet ready
- Pen and paper ready

**Call Opening:**
1. Professional greeting
2. State your name and company
3. Reference the specific load
4. Ask if available

**Information Gathering:**
- Confirm pickup details
- Confirm delivery details
- Ask about rate
- Special requirements?
- Payment terms?

**Negotiation Phase:**
- State your position
- Listen to response
- Counter professionally
- Find middle ground
- Know when to walk

**Closing:**
- Confirm all details verbally
- Request rate confirmation
- Provide driver info
- Set expectations for updates
- Thank them professionally

**Post-Call:**
- Document conversation
- Wait for rate confirmation
- Forward to driver
- Set pickup reminder

**Common Mistakes:**
- Not being prepared
- Talking too much
- Not listening
- Accepting first offer always
- Being unprofessional when rejected`
          },
          {
            id: "disp-m4-l2",
            title: "Email Communication Templates",
            duration: "25 min",
            content: `Professional email templates save time and ensure consistency.

**Introduction Email:**

Subject: Carrier Introduction - [Your Company Name]

Dear [Broker Name],

I'm reaching out to introduce [Company Name] as a reliable carrier option for your [origin] to [destination] lanes.

We operate [equipment type] and are interested in discussing regular freight opportunities.

Please find our carrier packet attached. We'd welcome the opportunity to work with you.

Best regards,
[Your Name]
[Phone] | [Email]

---

**Load Inquiry Email:**

Subject: Available for Load #[Number] - [Origin] to [Destination]

Hi [Name],

We have a truck available for your posted load:
- Load #: [Number]
- Origin: [City, State]
- Destination: [City, State]
- Equipment: [Type]

We can pick up [date/time]. Please confirm rate and send rate confirmation.

Best,
[Name]

---

**Delivery Confirmation:**

Subject: POD - Load #[Number] Delivered

[Name],

Load #[Number] delivered successfully:
- Delivery time: [Time]
- Signed by: [Name]
- POD attached

Please process payment per our agreement.

Thank you,
[Name]`
          }
        ]
      },
      {
        id: "disp-m5",
        title: "Carrier Packets",
        description: "Required documents and submission process",
        lessons: [
          {
            id: "disp-m5-l1",
            title: "Required Documents",
            duration: "30 min",
            content: `A complete carrier packet gets you set up with brokers faster.

**Essential Documents:**

*W-9 Form:*
- Tax identification
- Correct legal name
- Current address
- Signed and dated

*Certificate of Insurance:*
- Current coverage dates
- Liability: $1,000,000 minimum
- Cargo: $100,000 minimum
- Broker listed as certificate holder

*Operating Authority:*
- MC and DOT numbers
- Current and active status
- Screenshot from SAFER acceptable

*Signed Carrier Agreement:*
- Broker's contract terms
- Read carefully before signing
- Note payment terms
- Understand liability clauses

**Additional Documents (May Be Required):**
- Equipment list
- Driver information
- Safety rating documentation
- Additional insurance endorsements
- References

**Document Organization:**
- Keep digital copies organized
- Update when information changes
- Quick access for new setups
- Professional presentation

**Common Issues:**
- Expired insurance
- Mismatched names
- Missing signatures
- Outdated authority
- Poor quality scans`
          },
          {
            id: "disp-m5-l2",
            title: "Submission Process",
            duration: "25 min",
            content: `Smooth packet submission accelerates broker setup.

**Before Submission:**
- Verify all documents current
- Check broker requirements
- Ensure clean, readable scans
- Have follow-up plan ready

**Submission Methods:**

*Email Submission:*
- Most common method
- Single PDF preferred
- Clear subject line
- Professional message

*Online Portals:*
- Many brokers use these
- Follow instructions exactly
- Keep confirmation numbers
- Note username/password

*Fax (Some Still Use):*
- Confirm fax number
- Send cover sheet
- Follow up to confirm receipt

**Follow-Up Timeline:**
- Day 1: Submit packet
- Day 2-3: Follow up if no response
- Day 5: Second follow-up
- Beyond: Move on or try different contact

**After Setup:**
- Request confirmation
- Ask about available loads
- Note contact preferences
- Set up in your system

**Maintaining Packets:**
- Update insurance 30 days before expiration
- Notify of any changes
- Keep records of submissions
- Track broker relationships`
          }
        ]
      },
      {
        id: "disp-m6",
        title: "Load Planning",
        description: "Route optimization and time management",
        lessons: [
          {
            id: "disp-m6-l1",
            title: "Route Optimization",
            duration: "35 min",
            content: `Smart routing maximizes profit and driver satisfaction.

**Route Considerations:**

*Distance vs Time:*
- Shortest isn't always fastest
- Consider traffic patterns
- Highway vs surface streets
- Toll road decisions

*Fuel Stops:*
- Plan fuel locations
- Consider fuel prices by state
- Fuel network discounts
- DEF availability

*Rest Stops:*
- Safe parking locations
- Required break timing
- Overnight parking options
- Amenities availability

**Planning Tools:**
- Google Maps baseline
- PC Miler for trucking
- Truck-specific GPS
- State restriction databases

**Restrictions to Check:**
- Weight limits by state
- Bridge restrictions
- Tunnel restrictions
- Urban truck routes
- Seasonal road closures

**Optimization Tips:**
- Think about next load
- Avoid known congestion
- Consider time of day
- Weather impact
- Driver preferences

**Planning the Week:**
- Map multiple loads ahead
- Minimize empty miles
- Build consistent lanes
- Home time planning`
          },
          {
            id: "disp-m6-l2",
            title: "Time Management",
            duration: "30 min",
            content: `Managing time effectively keeps loads moving smoothly.

**Hours of Service Awareness:**

*Current Rules:*
- 11 hours driving after 10 off
- 14-hour on-duty window
- 30-minute break after 8 hours
- 70-hour/8-day limit

*Planning Around HOS:*
- Know driver's current clock
- Calculate arrival times
- Plan for breaks
- Buffer for delays

**Appointment Management:**

*Pickup Appointments:*
- Confirm timing
- Plan arrival 30 min early
- Have contingency plan
- Communicate delays immediately

*Delivery Appointments:*
- Know appointment type (first available vs exact)
- Calculate arrival time
- Plan for early arrival
- Document on-time delivery

**Communication Timing:**
- Morning check: driver status
- Pickup confirmation
- In-transit check (long hauls)
- Pre-delivery check
- Delivery confirmation

**Handling Delays:**
- Notify all parties immediately
- Provide new ETA
- Document cause
- Track for patterns

**Weekly Planning:**
- Sunday: Plan Monday loads
- Each day: Plan next day
- Thursday: Plan weekend coverage
- Ongoing: Track patterns`
          }
        ]
      },
      {
        id: "disp-m7",
        title: "Deadhead Reduction",
        description: "Strategies for maximizing profitability",
        lessons: [
          {
            id: "disp-m7-l1",
            title: "Strategy for Maximizing Profit",
            duration: "40 min",
            content: `Minimizing empty miles directly increases profitability.

**Understanding Deadhead:**
- Empty miles = no revenue
- Still costs fuel, time, wear
- Target: under 15% of total miles
- 20%+ deadhead hurts profits

**Prevention Strategies:**

*Pre-Planning:*
- Know where you're delivering
- Search for reload before arrival
- Build lanes with good backhauls
- Avoid known "black hole" areas

*Delivery Area Selection:*
- Choose loads to good freight areas
- Avoid areas with low outbound freight
- Consider rate vs positioning
- Think 2-3 loads ahead

*Timing:*
- Don't wait until empty to search
- Book reloads day before delivery
- Morning searches more productive
- Friday deliveries need Monday loads

**Calculations:**

*True Revenue Per Mile:*
Revenue ÷ Total Miles (loaded + empty)

Example:
- $2,000 load, 800 loaded miles
- 200 miles deadhead to pickup
- Total: 1,000 miles
- True RPM: $2.00 (not $2.50)

**When Deadhead Makes Sense:**
- To reach better freight area
- Getting home for time off
- Positioning for committed load
- Escaping bad market

**Tracking Deadhead:**
- Record every empty mile
- Calculate weekly percentages
- Identify problem patterns
- Adjust strategies accordingly`
          }
        ]
      },
      {
        id: "disp-m8",
        title: "Dispatch Workflow System",
        description: "Step-by-step process and daily task checklist",
        lessons: [
          {
            id: "disp-m8-l1",
            title: "Step-by-Step Dispatch Process",
            duration: "35 min",
            content: `A systematic workflow ensures nothing falls through the cracks.

**Load Lifecycle:**

**1. Load Identification**
- Search load boards
- Check broker relationships
- Evaluate rate and route
- Calculate profitability

**2. Load Booking**
- Negotiate rate
- Get verbal confirmation
- Receive rate confirmation
- Verify all details accurate

**3. Dispatch**
- Send info to driver
- Confirm receipt
- Verify driver understands
- Note pickup appointment

**4. Pickup**
- Confirm driver arrived
- Verify load matches description
- Get pickup number/BOL
- Note any issues

**5. Transit**
- Track location
- Check calls per requirement
- Handle any problems
- Update delivery ETA

**6. Delivery**
- Confirm arrival
- Verify successful delivery
- Collect POD
- Note delivery time

**7. Post-Delivery**
- Submit POD to broker
- Invoice if required
- Update records
- Plan next load

**8. Payment**
- Track invoice status
- Follow up as needed
- Reconcile payment
- Address discrepancies`
          },
          {
            id: "disp-m8-l2",
            title: "Daily Task Checklist",
            duration: "25 min",
            content: `Daily checklists ensure consistent execution.

**Morning Checklist (Start of Day):**
☐ Check driver location/status
☐ Review overnight deliveries
☐ Confirm today's pickups
☐ Identify trucks needing loads
☐ Check load board postings
☐ Review broker emails
☐ Update dispatch board

**Throughout Day:**
☐ Confirm each pickup
☐ Track in-transit loads
☐ Book future loads
☐ Handle problems immediately
☐ Document all communications
☐ Update load status
☐ Send check calls

**End of Day:**
☐ Verify all deliveries completed
☐ All loads have tomorrow's plan
☐ PODs submitted
☐ Any issues documented
☐ Tomorrow's pickups confirmed
☐ Driver schedules communicated
☐ Records updated

**Weekly Tasks:**
☐ Review settlements/payments
☐ Analyze lane performance
☐ Update broker relationships
☐ Check carrier insurance expiration
☐ Review deadhead percentage
☐ Plan for next week

**Monthly Tasks:**
☐ Revenue analysis
☐ Carrier performance review
☐ Broker relationship assessment
☐ Rate trend analysis
☐ Process improvement review`
          }
        ]
      },
      {
        id: "disp-m9",
        title: "Invoicing & Payment",
        description: "Factoring basics and broker payment timelines",
        lessons: [
          {
            id: "disp-m9-l1",
            title: "Factoring Basics",
            duration: "30 min",
            content: `Understanding factoring helps manage cash flow.

**What is Factoring?**
- Selling invoices for immediate payment
- Factor pays you (usually 95-98%)
- Factor collects from broker
- Factor keeps fee (2-5%)

**Why Use Factoring?**
- Immediate cash flow
- No waiting 30-45 days
- Credit risk transferred
- Collections handled for you
- Can haul more loads

**Factoring Process:**
1. Complete delivery
2. Submit POD and invoice to factor
3. Factor verifies delivery
4. Factor pays you (same/next day)
5. Factor collects from broker
6. Factor takes fee from final collection

**Factoring Costs:**
- Typically 2-5% of invoice
- Varies by volume and broker
- Some have flat fees
- Compare total cost carefully

**Choosing a Factor:**
- Recourse vs non-recourse
- Fee structure
- Funding speed
- Customer service
- Industry reputation
- Technology/portal

**Non-Recourse Factoring:**
- Factor takes credit risk
- Higher fees typically
- Protects against broker default

**Recourse Factoring:**
- You're responsible if broker doesn't pay
- Lower fees
- Your credit risk remains`
          },
          {
            id: "disp-m9-l2",
            title: "Broker Payment Timelines",
            duration: "25 min",
            content: `Understanding payment terms protects your cash flow.

**Typical Payment Terms:**

*Quick Pay (1-7 days):*
- Usually 2-3% fee
- Broker offers directly
- Good for cash flow
- Calculate if worth the fee

*Standard (15-30 days):*
- Most common terms
- No fee
- Plan cash flow accordingly
- Track invoice status

*Extended (45+ days):*
- Some larger brokers
- May not be worth it
- Factor if needed
- Be cautious

**Invoice Requirements:**
- Rate confirmation copy
- Bill of lading
- Proof of delivery
- Proper formatting
- Correct remittance info

**Tracking Payments:**
- Log invoice date
- Note expected payment date
- Follow up at 30+ days
- Document all communication
- Escalate if needed

**When Payments Are Late:**
- Day 31: Friendly follow-up
- Day 45: Firm follow-up
- Day 60: Escalate to management
- Day 90+: Consider collection action

**Protecting Yourself:**
- Check broker credit before hauling
- Get rate confirmation signed
- Keep all documentation
- Know your rights
- Consider factoring for risky brokers`
          }
        ]
      }
    ]
  },
  {
    id: "trucking-101",
    title: "Trucking 101",
    subtitle: "Authority Startup Blueprint",
    description: "Guide new carriers through starting and stabilizing their trucking business. From authority basics to scaling operations.",
    price: 250,
    duration: "6 weeks",
    level: "Beginner",
    category: "Business",
    icon: "Briefcase",
    color: "#CD7F32",
    features: [
      "Cost-per-mile calculator",
      "90-day checklist",
      "Maintenance tracker",
      "Authority setup guide",
      "Insurance structure overview",
      "Scaling strategies"
    ],
    outcomes: [
      "Navigate the authority process confidently",
      "Structure your business correctly",
      "Secure proper insurance coverage",
      "Understand compliance requirements",
      "Calculate true cost per mile",
      "Plan your first 90 days strategically"
    ],
    modules: [
      {
        id: "truck-m1",
        title: "Starting a Trucking Company",
        description: "Authority basics and business setup fundamentals",
        lessons: [
          {
            id: "truck-m1-l1",
            title: "Authority Basics",
            duration: "35 min",
            content: `Understanding operating authority is the first step to your trucking business.

**What is Operating Authority?**
- Federal permission to haul freight for hire
- Issued by FMCSA
- Required for interstate commerce
- Your license to operate

**Types of Authority:**

*Motor Carrier (MC Number):*
- Required to transport property
- Common carrier: general public
- Contract carrier: specific shippers

*Broker Authority:*
- For arranging transportation
- Doesn't involve owning trucks
- Different requirements

*Freight Forwarder:*
- Arranges shipments
- Uses various carriers
- Different focus than carrier

**USDOT Number:**
- Identification number (not authority)
- Required for all commercial vehicles
- Interstate operation
- Vehicles over 10,001 lbs

**Authority vs. Leasing:**

*Own Authority:*
- Full independence
- Higher insurance costs
- Direct broker relationships
- Complete responsibility

*Leased to Carrier:*
- Operate under their authority
- Lower insurance (usually)
- Less independence
- Carrier takes percentage`
          },
          {
            id: "truck-m1-l2",
            title: "Business Setup",
            duration: "40 min",
            content: `Proper business structure protects you and sets up for success.

**Business Structure Options:**

*Sole Proprietorship:*
- Simplest, but risky
- No liability protection
- Personal assets at risk
- Not recommended for trucking

*LLC (Limited Liability Company):*
- Recommended for most
- Liability protection
- Tax flexibility
- Professional appearance
- Relatively simple setup

*Corporation:*
- More complex
- Strongest protection
- Consider for growth plans
- Higher maintenance costs

**Formation Steps:**

1. Choose business name
2. Check name availability
3. File with state (LLC articles)
4. Get EIN from IRS (free)
5. Open business bank account
6. Set up accounting system

**Essential Accounts:**
- Business checking
- Savings for taxes
- Emergency fund account
- Credit card (business)

**Business Address:**
- Needed for authority
- Can use home address
- Consider registered agent
- Keep consistent everywhere

**Initial Costs:**
- State filing: $50-500
- EIN: Free
- Business account: Usually free
- Registered agent: $50-300/year
- Total: $100-800 typically`
          }
        ]
      },
      {
        id: "truck-m2",
        title: "FMCSA & DOT Overview",
        description: "Regulations and registration requirements",
        lessons: [
          {
            id: "truck-m2-l1",
            title: "Regulations",
            duration: "40 min",
            content: `Understanding federal regulations keeps you compliant and operating.

**Key Regulatory Bodies:**

*FMCSA (Federal Motor Carrier Safety Administration):*
- Sets safety regulations
- Issues authority
- Enforces compliance
- Maintains SAFER database

*DOT (Department of Transportation):*
- Oversees FMCSA
- Broader transportation policy
- Vehicle standards
- Infrastructure

**Major Regulations:**

*Hours of Service:*
- 11-hour driving limit
- 14-hour on-duty window
- 10-hour off-duty required
- 70-hour/8-day limit
- 30-minute break rule

*Drug & Alcohol Testing:*
- Pre-employment required
- Random testing
- Post-accident testing
- Clearinghouse reporting

*Vehicle Standards:*
- Inspection requirements
- Maintenance standards
- Weight limits
- Size restrictions

*Driver Qualifications:*
- CDL requirements
- Medical certification
- Age requirements
- English proficiency

**Penalties:**
- Fines per violation
- Out-of-service orders
- Authority revocation
- Criminal charges (severe)

**Staying Compliant:**
- Know the rules
- Document everything
- Regular audits
- Stay updated on changes`
          },
          {
            id: "truck-m2-l2",
            title: "Registration Requirements",
            duration: "35 min",
            content: `Complete all registrations before operating.

**Federal Registrations:**

*USDOT Number:*
- Apply at FMCSA portal
- Free registration
- Required for all
- Biennial update required

*MC Authority:*
- Apply online (OP-1 form)
- $300 filing fee
- 3-week waiting period
- Insurance required to activate

*UCR (Unified Carrier Registration):*
- Annual registration
- Fee based on fleet size
- Required before operating
- Renew by deadline

**State Registrations:**

*IRP (International Registration Plan):*
- Apportioned registration
- Based on miles per state
- Replaces individual plates
- Annual renewal

*IFTA (International Fuel Tax Agreement):*
- Fuel tax agreement
- Quarterly reporting
- File even with no miles
- Penalties for late filing

**Other Requirements:**

*BOC-3 (Process Agent):*
- Required for authority
- Agent in each state
- Usually blanket service
- One-time filing

*Heavy Vehicle Use Tax (2290):*
- Annual IRS filing
- Trucks 55,000+ lbs
- Due by August 31
- Required for registration

**Timeline:**
- Allow 4-6 weeks total
- Insurance before authority activates
- Don't operate until complete
- Document everything`
          }
        ]
      },
      {
        id: "truck-m3",
        title: "Insurance Structure",
        description: "Types of coverage and cost considerations",
        lessons: [
          {
            id: "truck-m3-l1",
            title: "Types of Coverage",
            duration: "40 min",
            content: `Understanding insurance options protects your business.

**Required Insurance:**

*Primary Liability:*
- Minimum: $750,000
- Higher for hazmat
- Covers bodily injury/property damage
- Required for authority

*Cargo Insurance:*
- Typically: $100,000
- Covers freight damage/loss
- Required by most brokers
- Check commodity requirements

**Recommended Insurance:**

*Physical Damage:*
- Covers your truck
- Collision and comprehensive
- Usually required if financed
- Deductibles affect premium

*Bobtail/Non-Trucking:*
- Coverage when not dispatched
- Personal use of truck
- Deadheading to pickups
- Fills gaps in primary

*Occupational Accident:*
- Injury/disability coverage
- Workers' comp alternative
- Important for owner-operators
- Check policy details

*General Liability:*
- Non-trucking business coverage
- Slip and fall, etc.
- Office/premises coverage
- Recommended for all

**Specialty Coverage:**
- Trailer interchange
- Pollution liability
- Cyber liability
- Employment practices`
          },
          {
            id: "truck-m3-l2",
            title: "Cost Considerations",
            duration: "35 min",
            content: `Managing insurance costs while maintaining protection.

**Cost Factors:**

*High Impact:*
- Driving record (biggest factor)
- Years of experience
- Type of freight
- Operating radius
- Equipment value/age

*Medium Impact:*
- Credit score
- Business history
- Deductible choices
- Coverage limits

*Lower Impact:*
- Safety equipment
- Training programs
- Association membership

**Typical Costs (New Authority):**

*Primary Liability:*
- $12,000-25,000/year
- Higher for new authority
- Decreases with clean record

*Cargo:*
- $1,500-3,000/year
- Varies by commodity

*Physical Damage:*
- 3-8% of truck value
- Higher for new trucks

*Total First Year:*
- $15,000-35,000 typical
- Plan accordingly

**Reducing Costs:**

*Immediate:*
- Shop multiple companies
- Increase deductibles
- Lower coverage limits (carefully)
- Pay annually vs monthly

*Over Time:*
- Maintain clean record
- Build claims-free history
- Establish business credit
- Consider associations

**Finding Insurance:**
- Use trucking specialists
- Get multiple quotes
- Compare exact coverage
- Check insurer ratings`
          }
        ]
      },
      {
        id: "truck-m4",
        title: "ELD & Compliance Setup",
        description: "Logging requirements and device setup",
        lessons: [
          {
            id: "truck-m4-l1",
            title: "Logging Requirements",
            duration: "35 min",
            content: `Electronic logging is mandatory for most commercial drivers.

**ELD Mandate:**
- Required since December 2019
- Replaces paper logs
- Automatic recording
- Limited exceptions

**Who Needs ELD:**
- Most property-carrying drivers
- Interstate commerce
- Subject to HOS rules
- 8+ days in 30-day period

**Exemptions:**
- Drivers using short-haul exception
- Driveaway-towaway operations
- Vehicles manufactured before 2000
- Some agricultural operations

**What ELD Records:**
- Date and time
- Location information
- Engine hours
- Vehicle miles
- Driver identification
- Vehicle identification
- Motor carrier identification

**Driver Responsibilities:**
- Review records daily
- Certify logs accurate
- Report malfunctions
- Keep supporting documents
- Make required annotations

**Enforcement:**
- Roadside inspections
- Data transfer to officers
- Violations recorded
- Out-of-service for serious issues`
          },
          {
            id: "truck-m4-l2",
            title: "Device Setup",
            duration: "30 min",
            content: `Proper ELD setup ensures compliance and functionality.

**Choosing an ELD:**

*Considerations:*
- FMCSA registration (required)
- Monthly/upfront costs
- Features needed
- Customer support
- User reviews

*Popular Options:*
- KeepTruckin/Motive
- Samsara
- BigRoad
- Garmin eLog
- Many others

**Installation:**

*Hardware:*
- Connects to engine port
- Usually diagnostic port
- Follow manufacturer instructions
- Test after installation

*Software:*
- Download driver app
- Create driver account
- Link to company account
- Test functionality

**Training:**
- Learn interface
- Practice logging
- Understand duty status
- Know how to annotate
- Emergency procedures

**Common Issues:**
- GPS accuracy
- Connection problems
- Duty status errors
- Malfunction procedures

**Maintaining Compliance:**
- Keep ELD updated
- Monitor for malfunctions
- Review logs regularly
- Keep backup documentation`
          }
        ]
      },
      {
        id: "truck-m5",
        title: "IFTA & Fuel Management",
        description: "Fuel tax basics and tracking fuel usage",
        lessons: [
          {
            id: "truck-m5-l1",
            title: "Fuel Tax Basics",
            duration: "30 min",
            content: `Understanding IFTA keeps you compliant with fuel taxes.

**What is IFTA?**
- International Fuel Tax Agreement
- Agreement between US states and Canadian provinces
- Simplifies fuel tax reporting
- One license, one report

**How It Works:**
- You pay fuel tax where you buy fuel
- You owe fuel tax where you drive
- IFTA reconciles the difference
- You pay or get refund quarterly

**Requirements:**

*Who Needs IFTA:*
- Qualified motor vehicles
- Two or more jurisdictions
- GVWR over 26,000 lbs or 3+ axles

*What You Need:*
- IFTA license and decals
- Quarterly fuel tax reports
- Records of all fuel purchases
- Miles traveled by jurisdiction

**Quarterly Reports:**
- Due last day of month after quarter
- Q1 (Jan-Mar): Due April 30
- Q2 (Apr-Jun): Due July 31
- Q3 (Jul-Sep): Due October 31
- Q4 (Oct-Dec): Due January 31

**Penalties:**
- Interest on late payments
- Fines for missing reports
- License revocation possible
- Audit risk increases`
          },
          {
            id: "truck-m5-l2",
            title: "Tracking Fuel Usage",
            duration: "25 min",
            content: `Good fuel tracking saves money and ensures accurate reporting.

**What to Track:**

*Every Fuel Purchase:*
- Date
- Vendor name and location
- Gallons purchased
- Price per gallon
- Total amount

*Miles by Jurisdiction:*
- State/province entry/exit
- Miles in each
- Route documentation

**Tracking Methods:**

*Manual:*
- Fuel receipt log
- Trip sheet for miles
- Spreadsheet calculation
- Time-consuming but works

*Electronic:*
- ELD can track miles
- Fuel card reports
- IFTA software
- Integrates data automatically

**Fuel Cards:**
- Discount at participating stations
- Consolidated reporting
- IFTA data available
- Fraud protection

**Best Practices:**
- Save all receipts
- Record immediately
- Reconcile regularly
- Back up data
- Use consistent system

**Calculating MPG:**
- Important for IFTA accuracy
- Total miles ÷ Total gallons
- Track trends over time
- Investigate significant changes`
          }
        ]
      },
      {
        id: "truck-m6",
        title: "Cost Per Mile",
        description: "Expense breakdown and profit calculation",
        lessons: [
          {
            id: "truck-m6-l1",
            title: "Expense Breakdown",
            duration: "40 min",
            content: `Knowing every expense helps you price loads profitably.

**Fixed Costs (Monthly):**

*Truck Payment:*
- $1,500-3,500 typical
- Includes any trailer payment
- Factor in depreciation too

*Insurance:*
- $1,000-2,500 monthly
- All types combined
- Review annually

*Permits/Authority:*
- $100-300 monthly average
- IRP, UCR, HVUT spread out
- Factor in renewals

*Communication/Technology:*
- $100-300 monthly
- Phone, ELD, software

*Parking/Storage:*
- $0-500 monthly
- If applicable

**Variable Costs (Per Mile):**

*Fuel:*
- Your biggest variable
- Calculate: fuel cost ÷ MPG
- Currently $0.40-0.70/mile

*Maintenance:*
- Budget $0.10-0.20/mile
- Covers tires, oil, repairs
- Will vary by truck age

*Tolls:*
- Varies by region
- $0.02-0.10/mile average
- Track actual for accuracy

**Calculating Total Cost Per Mile:**

Formula:
(Fixed Monthly ÷ Monthly Miles) + Variable Per Mile = Cost Per Mile

Example:
$5,000 fixed ÷ 10,000 miles = $0.50
+ $0.70 variable = $1.20 per mile total`
          },
          {
            id: "truck-m6-l2",
            title: "Profit Calculation",
            duration: "30 min",
            content: `Understanding profit helps you make better decisions.

**Revenue Calculation:**
- Line haul rate
- + Fuel surcharge
- + Accessorials (detention, etc.)
- = Total Revenue

**Profit Calculation:**
- Total Revenue
- - All Costs
- = Net Profit

**Per Load Analysis:**

Example Load:
- Revenue: $2,500
- Loaded miles: 1,000
- Deadhead: 200
- Total miles: 1,200
- Cost per mile: $1.20
- Total cost: $1,440
- Net profit: $1,060
- Effective RPM: $2.08

**Minimum Rate Calculation:**

You need: Cost + Desired Profit

Example:
- Cost per mile: $1.20
- Want $0.50 profit: Minimum $1.70/mile
- On 1,000 miles: $1,700 minimum rate

**Margin Goals:**
- 10-15% margin: surviving
- 20-30% margin: healthy
- 30%+ margin: excellent

**Tracking Profitability:**
- Track every load
- Calculate actual vs target
- Analyze patterns
- Adjust strategies

**Don't Forget:**
- Taxes (25-30% of profit)
- Emergency fund
- Owner salary
- Equipment replacement`
          }
        ]
      },
      {
        id: "truck-m7",
        title: "Maintenance Planning",
        description: "Preventative maintenance and cost control",
        lessons: [
          {
            id: "truck-m7-l1",
            title: "Preventative Maintenance",
            duration: "35 min",
            content: `Preventative maintenance saves money and prevents breakdowns.

**Service Intervals:**

*Oil & Filter:*
- Every 15,000-25,000 miles
- Check manufacturer specs
- Use quality oil
- Don't stretch intervals

*Fuel Filters:*
- Every 30,000-50,000 miles
- Critical for engine health
- Replace both if applicable

*Air Filter:*
- Check every 15,000 miles
- Replace as needed
- Conditions vary

*Coolant:*
- Check level weekly
- System flush: per manufacturer
- Test for proper mixture

**Tire Management:**
- Check pressure daily
- Rotate per wear pattern
- Tread depth monitoring
- Alignment checks

**Brake Service:**
- Inspect at every service
- Adjust as needed
- Replace linings before metal
- Don't defer brake work

**Transmission/Drivetrain:**
- Fluid changes per manufacturer
- U-joint greasing
- Listen for unusual sounds

**PM Schedule Example:**

*Weekly:*
- Fluid levels
- Tire pressure
- Light check
- Visual inspection

*Monthly:*
- Detailed inspection
- Grease fittings
- Battery check

*Quarterly:*
- Professional inspection
- Minor services

*Annually:*
- Major service
- DOT inspection`
          },
          {
            id: "truck-m7-l2",
            title: "Cost Control",
            duration: "30 min",
            content: `Smart maintenance decisions control costs without sacrificing reliability.

**Finding Good Service:**
- Build relationship with shop
- Get recommendations
- Compare labor rates
- Ask about warranties

**DIY vs Professional:**

*Owner Can Do:*
- Fluid checks
- Air filter
- Light bulbs
- Basic inspections
- Tire pressure

*Professional Recommended:*
- Engine work
- Transmission
- Brakes
- Electrical issues
- Anything complex

**Parts Decisions:**
- OEM vs aftermarket
- Quality matters long-term
- Warranty considerations
- Critical parts: don't cheap out

**Emergency Repairs:**
- Have funds available
- Know roadside services
- Keep emergency contacts
- Document for future

**Tracking Maintenance:**
- Log all service
- Track costs by category
- Monitor trends
- Predict future needs

**Cost Reduction Tips:**
- Follow PM schedule (prevents big repairs)
- Address issues early
- Shop labor rates
- Buy quality parts
- Learn basic tasks

**Budgeting:**
- $0.10-0.20 per mile reserve
- More for older trucks
- Review quarterly
- Adjust as needed`
          }
        ]
      },
      {
        id: "truck-m8",
        title: "First 90 Days Strategy",
        description: "Key milestones and avoiding shutdown risks",
        lessons: [
          {
            id: "truck-m8-l1",
            title: "Key Milestones",
            duration: "35 min",
            content: `The first 90 days determine your business success.

**Days 1-30: Foundation**

*Week 1:*
☐ Authority active and verified
☐ Insurance confirmed
☐ ELD installed and tested
☐ First loads booked

*Week 2:*
☐ Broker relationships started
☐ Dispatch workflow established
☐ First revenue earned
☐ Initial challenges identified

*Week 3-4:*
☐ Consistent loads running
☐ Cash flow stabilizing
☐ Systems improving
☐ First month finances tracked

**Days 31-60: Stabilization**

*Goals:*
☐ Positive cash flow achieved
☐ Broker network growing
☐ Operational rhythm established
☐ Issues being resolved
☐ First IFTA quarter prepared
☐ Maintenance schedule followed

**Days 61-90: Growth**

*Goals:*
☐ Consistent profitability
☐ Strong broker relationships
☐ Systems optimized
☐ Planning for future
☐ Building reserves
☐ Evaluating performance

**Key Metrics to Track:**
- Revenue per week
- Miles driven
- Cost per mile
- Profit margin
- Deadhead percentage
- Days without load`
          },
          {
            id: "truck-m8-l2",
            title: "Avoiding Shutdown Risks",
            duration: "30 min",
            content: `Avoid these common mistakes that shut down new carriers.

**Cash Flow Killers:**
- Not understanding payment terms
- Overextending before revenue
- No emergency fund
- Poor expense tracking
- Living beyond means

**Solutions:**
- Use factoring if needed
- Start with reserves
- Track every dollar
- Keep personal expenses low
- Know your breakeven

**Compliance Failures:**
- Missing IFTA filings
- Lapsed insurance
- UCR not renewed
- HOS violations
- Drug testing missed

**Solutions:**
- Calendar all deadlines
- Set reminders
- Don't skip or delay
- Build compliance into routine

**Operational Failures:**
- Taking bad loads
- Deadhead disasters
- Equipment breakdowns
- Customer service issues

**Solutions:**
- Calculate every load
- Plan routes carefully
- Maintain equipment
- Communicate professionally

**Personal Failures:**
- Burnout
- Health issues
- Family stress
- Isolation

**Solutions:**
- Plan home time
- Take care of health
- Communicate with family
- Build support network

**Warning Signs:**
- Consistently below target
- Growing debt
- Compliance issues
- Health problems
- Family problems

**When to Pivot:**
- After 90 days of losses
- When compliance at risk
- If health suffers
- When family suffers
- Before bankruptcy`
          }
        ]
      },
      {
        id: "truck-m9",
        title: "Scaling Operations",
        description: "Growing from one truck to a fleet",
        lessons: [
          {
            id: "truck-m9-l1",
            title: "From 1 Truck to Fleet",
            duration: "35 min",
            content: `Growth requires planning and systems.

**When to Consider Growth:**
- Consistently profitable (6+ months)
- Strong broker relationships
- Systems working well
- Capital available
- Time and capacity

**Growth Options:**

*Add Trucks (Owner-Operator):*
- Buy/lease additional truck
- Hire driver
- Higher risk, higher reward
- Full control

*Lease Trucks On:*
- Other owners work under you
- Less capital required
- Shared risk/reward
- Less control

**Financial Requirements:**
- Down payment for truck
- Insurance increase
- Working capital doubled
- Reserve fund larger

**Operational Changes:**
- Dispatch for multiple trucks
- Driver management
- More broker relationships
- Systems must scale

**Before Adding:**
☐ Current truck profitable?
☐ Systems documented?
☐ Capital secured?
☐ Insurance arranged?
☐ Freight demand confirmed?
☐ Time to manage?

**Start Small:**
- One truck at a time
- Prove model works
- Build systems
- Then expand further`
          },
          {
            id: "truck-m9-l2",
            title: "Hiring Drivers",
            duration: "30 min",
            content: `Hiring drivers is the biggest challenge in growing.

**Finding Drivers:**
- Job boards (Indeed, CDL-specific)
- Referrals from current drivers
- Trucking schools
- Social media
- Industry events

**Driver Qualification File:**

*Required Documents:*
- Application for employment
- Motor vehicle record (MVR)
- Employment verification (3 years)
- Drug test results
- Medical certificate
- Road test certificate (or CDL)

**Interview Process:**
- Verify documentation
- Check references
- Assess communication
- Discuss expectations
- Road test (recommended)

**Compensation Options:**

*Percentage:*
- Driver gets % of load
- Motivates maximizing revenue
- Complicated accounting

*Mileage:*
- Cents per mile
- Simple and predictable
- Common in industry

*Hourly:*
- Less common OTR
- Local delivery often

**Setting Expectations:**
- Communication requirements
- Equipment care standards
- Compliance requirements
- Performance expectations

**Retention:**
- Competitive pay
- Respect and communication
- Good equipment
- Home time planning
- Growth opportunities

**Common Mistakes:**
- Hiring too fast
- Skipping verification
- Unclear expectations
- Poor communication
- Equipment problems`
          }
        ]
      }
    ]
  },
  {
    id: "freight-broker-101",
    title: "Freight Broker 101",
    subtitle: "Freight Coordination & Sales Fundamentals",
    description: "Learn how to operate as a freight broker and connect shippers with carriers. Master the business of freight coordination.",
    price: 250,
    duration: "8 weeks",
    level: "Intermediate",
    category: "Business",
    icon: "Building2",
    color: "#3B82F6",
    features: [
      "Email templates",
      "Call scripts",
      "Workflow checklist",
      "Authority setup guide",
      "Shipper acquisition strategies",
      "Carrier vetting process"
    ],
    outcomes: [
      "Obtain and activate broker authority",
      "Develop shipper sales strategies",
      "Build a reliable carrier network",
      "Master rate negotiation",
      "Create efficient load coordination workflows",
      "Build repeat business relationships"
    ],
    modules: [
      {
        id: "broker-m1",
        title: "Broker Role & Responsibilities",
        description: "What brokers do and industry positioning",
        lessons: [
          {
            id: "broker-m1-l1",
            title: "What Brokers Do",
            duration: "35 min",
            content: `Understanding the broker's role in the freight industry.

**Core Function:**
- Connect shippers with carriers
- Don't own or operate trucks
- Arrange and manage transportation
- Handle documentation and payment
- Solve problems that arise

**The Broker's Place:**

Shipper → Broker → Carrier

*Broker adds value by:*
- Finding capacity quickly
- Vetting carriers
- Handling paperwork
- Providing single point of contact
- Solving problems

**Daily Activities:**
- Finding shipper freight
- Matching with carriers
- Negotiating rates
- Managing shipments in transit
- Handling documentation
- Resolving issues
- Invoicing and payment

**Revenue Model:**
- Charge shipper: $2,500
- Pay carrier: $2,100
- Gross margin: $400 (16%)
- Must cover overhead and profit

**Types of Brokers:**

*Large Brokerages:*
- CH Robinson, TQL, Landstar
- High volume operations
- Technology platforms
- Multiple offices

*Small/Medium:*
- Independent operations
- Niche focuses
- Personal relationships
- Flexible service

*Agent-Based:*
- Agents under larger authority
- Lower startup cost
- Split commissions
- Support provided`
          },
          {
            id: "broker-m1-l2",
            title: "Industry Positioning",
            duration: "30 min",
            content: `Finding your place in the competitive brokerage market.

**Market Overview:**
- Highly competitive industry
- Low barriers to entry
- Relationship-driven business
- Technology increasingly important

**Positioning Options:**

*Generalist:*
- Handle any freight
- Wide market potential
- More competition
- Less specialization advantage

*Niche Specialist:*
- Specific commodities
- Specific lanes
- Specific industries
- Less competition, deeper expertise

**Competitive Advantages:**

*Service:*
- Responsiveness
- Problem-solving
- Communication quality
- Reliability

*Specialization:*
- Industry knowledge
- Lane expertise
- Equipment specialization
- Compliance expertise

*Technology:*
- Tracking capabilities
- Communication tools
- Reporting
- Integration

*Relationships:*
- Shipper trust
- Carrier network
- Industry connections

**Your Niche:**
Questions to answer:
- What industries do I know?
- What regions do I know?
- What relationships do I have?
- What makes me different?

**Market Entry Strategy:**
- Start with what you know
- Build from relationships
- Add capabilities over time
- Don't try to be everything`
          }
        ]
      },
      {
        id: "broker-m2",
        title: "Broker Authority Setup",
        description: "Licensing basics and bond requirements",
        lessons: [
          {
            id: "broker-m2-l1",
            title: "Licensing Basics",
            duration: "40 min",
            content: `Getting your broker authority is the first step to operating legally.

**What is Broker Authority?**
- Federal license to arrange transportation
- Issued by FMCSA
- Different from motor carrier authority
- Required before operating

**Application Process:**

*Step 1: Business Setup*
- Form LLC or corporation
- Get EIN from IRS
- Open business bank account

*Step 2: USDOT Number*
- Apply at FMCSA website
- Free registration
- Immediate issuance

*Step 3: Broker Authority*
- File OP-1 application
- $300 filing fee
- Online submission
- 3-week processing

*Step 4: Surety Bond*
- $75,000 bond required
- File BMC-84 form
- Must be filed before activation

*Step 5: Process Agent*
- BOC-3 filing required
- Agent in each state
- Use blanket service

*Step 6: Activation*
- After all filings complete
- Verify on SAFER system
- Ready to operate

**Timeline:**
- 4-6 weeks typical
- Can be faster with preparation
- Don't operate until active

**Costs:**
- Authority: $300
- Bond: $500-2,000/year
- Process agent: $50-100
- Total: $850-2,400 first year`
          },
          {
            id: "broker-m2-l2",
            title: "Bond Requirements",
            duration: "30 min",
            content: `Understanding the surety bond requirement.

**What is the Broker Bond?**
- Financial guarantee
- Protects shippers and carriers
- $75,000 minimum
- Required for authority

**How It Works:**
- You pay premium to surety
- Surety provides bond to FMCSA
- If you don't pay carriers/shippers
- They can file claim against bond

**Getting a Bond:**

*What Sureties Consider:*
- Credit score (biggest factor)
- Industry experience
- Financial statements
- Business plan
- Personal guarantee

*Typical Process:*
1. Application submission
2. Credit check
3. Financial review
4. Approval/decline
5. Premium quote
6. Payment and issuance
7. Filing with FMCSA

**Premium Costs:**
- Good credit: 1-3% of bond ($750-2,250)
- Average credit: 3-5% ($2,250-3,750)
- Poor credit: 5-15% or decline

**Maintaining Your Bond:**
- Pay premium on time
- Renew before expiration
- Avoid claims
- Maintain good credit

**Bond Claims:**
- Filed by unpaid parties
- Investigated by surety
- If valid, surety pays
- You reimburse surety
- Affects future bonding

**Alternatives:**
- Trust fund ($75,000 cash)
- Same protection
- Ties up capital
- May be option for poor credit`
          }
        ]
      },
      {
        id: "broker-m3",
        title: "Finding Shippers",
        description: "Outreach strategies and building a client base",
        lessons: [
          {
            id: "broker-m3-l1",
            title: "Outreach Strategies",
            duration: "40 min",
            content: `Finding shippers is the core challenge of brokerage.

**Where to Find Prospects:**

*Online Research:*
- Company websites
- LinkedIn
- Industry directories
- Import/export databases
- Business registrations

*Networking:*
- Industry associations
- Trade shows
- Chamber of commerce
- Professional groups
- Referrals

*Direct Observation:*
- Industrial areas
- Manufacturing zones
- Distribution centers
- Loading docks

**Outreach Methods:**

*Cold Calling:*
- Most direct approach
- Requires thick skin
- Numbers game
- Skills improve with practice

*Email Campaigns:*
- Less intrusive
- Can scale
- Lower response rate
- Follow-up important

*Direct Mail:*
- Stands out from digital
- More expensive
- Good for local focus

*In-Person Visits:*
- Highest impact
- Time intensive
- Best for local prospects

**Qualification Questions:**
- What do you ship?
- How often do you ship?
- Where do you ship to?
- What's your current situation?
- What are your pain points?
- Who makes transportation decisions?

**Tracking Outreach:**
- CRM or spreadsheet
- Log every contact
- Note next steps
- Follow up systematically
- Measure conversion`
          },
          {
            id: "broker-m3-l2",
            title: "Building a Client Base",
            duration: "35 min",
            content: `Turning prospects into loyal customers.

**The Sales Process:**

*1. Prospect:*
Identify potential shippers

*2. Qualify:*
Determine fit and potential

*3. Connect:*
Make initial contact

*4. Discover:*
Learn their needs

*5. Present:*
Show how you solve problems

*6. Handle Objections:*
Address concerns

*7. Close:*
Get the first shipment

*8. Deliver:*
Execute flawlessly

*9. Grow:*
Expand the relationship

**Common Objections:**

*"We're happy with our current broker"*
- Ask what they like
- Offer to be backup
- Stay in touch

*"Your rates are too high"*
- Discuss total value
- Explain service difference
- Consider trial pricing

*"We'll think about it"*
- Ask what they need to decide
- Set follow-up time
- Provide additional info

**First Shipment Success:**
- Over-communicate
- Deliver perfectly
- Thank them after
- Ask for feedback
- Request more opportunities

**Growing Relationships:**
- Regular communication
- Expand to new lanes
- Introduce to new services
- Ask for referrals
- Quarterly reviews`
          }
        ]
      },
      {
        id: "broker-m4",
        title: "Carrier Relationships",
        description: "Vetting carriers and building trust",
        lessons: [
          {
            id: "broker-m4-l1",
            title: "Vetting Carriers",
            duration: "35 min",
            content: `Proper carrier vetting protects you and your shippers.

**Why Vetting Matters:**
- Liability protection
- Service quality
- Fraud prevention
- Regulatory compliance
- Reputation protection

**Minimum Requirements:**
- Active MC authority
- Current insurance
- Satisfactory safety rating
- Clean inspection history

**Verification Process:**

*Authority:*
- Check SAFER website
- Verify active status
- Note any limitations
- Check for recent changes

*Insurance:*
- Current certificates
- Adequate coverage
- Your company as certificate holder
- Verify with carrier's insurer

*Safety:*
- CSA scores review
- Inspection history
- Out-of-service rate
- Crash history

**Documentation:**

*Carrier Packet:*
- W-9
- Certificate of Insurance
- Authority documents
- Signed broker-carrier agreement
- Equipment list

**Red Flags:**
- Very new authority (< 6 months)
- No or poor references
- Insurance issues
- Poor safety scores
- Reluctant to provide info
- Rates way below market

**Ongoing Monitoring:**
- Insurance expiration alerts
- Authority status checks
- Performance tracking
- Safety updates`
          },
          {
            id: "broker-m4-l2",
            title: "Building Trust",
            duration: "30 min",
            content: `Strong carrier relationships ensure capacity when you need it.

**What Carriers Want:**
- Consistent freight
- Fair rates
- Quick payment
- Accurate information
- Respectful treatment
- Problem resolution

**Building Relationships:**

*First Load:*
- Accurate information
- Fair rate
- Clear expectations
- Good communication
- Quick payment

*Growing Trust:*
- Repeat business
- Consistent treatment
- Reliable payment
- Help with problems
- Respect their time

**Communication Standards:**

*Load Tendering:*
- Complete information
- Reasonable timing
- Written confirmation

*During Transit:*
- Check calls as needed
- Responsive to issues
- Updates provided

*After Delivery:*
- Quick POD processing
- Prompt payment
- Performance feedback

**Preferred Carrier Status:**
- Best loads first
- Better rates over time
- Priority communication
- Problem flexibility
- Relationship value

**Payment Impact:**
- Quick pay builds loyalty
- Slow pay loses carriers
- Be consistent
- Communicate if issues

**Network Size:**
- Quality over quantity
- Build depth in key lanes
- Have backup options
- Maintain relationships`
          }
        ]
      },
      {
        id: "broker-m5",
        title: "Rate Negotiation",
        description: "Pricing loads and margin strategy",
        lessons: [
          {
            id: "broker-m5-l1",
            title: "Pricing Loads",
            duration: "35 min",
            content: `Effective pricing balances shipper and carrier needs.

**Understanding Costs:**

*Carrier Costs:*
- Fuel
- Driver pay
- Insurance
- Equipment
- Overhead

*Market Factors:*
- Supply and demand
- Lane balance
- Seasonal patterns
- Equipment availability

**Rate Research:**
- DAT RateView
- Truckstop data
- Historical quotes
- Carrier feedback
- Market experience

**Pricing Strategy:**

*Shipper Rate:*
- Market research
- Competitor awareness
- Service value
- Relationship history
- Volume consideration

*Carrier Rate:*
- Market rate
- Carrier relationship
- Load characteristics
- Timing factors

*Your Margin:*
= Shipper Rate - Carrier Rate - Overhead

**Quoting Shippers:**
- Know your floor (minimum margin)
- Consider volume potential
- Factor service requirements
- Leave negotiation room
- Be competitive but profitable

**Paying Carriers:**
- Know market rates
- Be fair consistently
- Consider relationship value
- Don't race to bottom`
          },
          {
            id: "broker-m5-l2",
            title: "Margin Strategy",
            duration: "30 min",
            content: `Managing margins determines profitability.

**Understanding Margin:**

Gross Margin = Revenue - Carrier Cost
Net Margin = Gross Margin - Operating Costs

**Typical Margins:**
- Spot market: 12-20%
- Contract freight: 8-15%
- High service: 15-25%
- Commoditized: 5-10%

**Factors Affecting Margin:**

*Higher Margin:*
- Specialized freight
- Time-critical shipments
- Problem-solving required
- Strong relationships
- Good service

*Lower Margin:*
- Commodity freight
- High competition
- Price-only buyers
- Tight markets

**Margin Protection:**
- Know your costs
- Set floor margins
- Track by lane/customer
- Review regularly
- Adjust strategies

**Volume vs Margin:**
- High volume, low margin can work
- Low volume needs higher margin
- Balance is key
- Don't buy business unprofitably

**Improving Margins:**
- Better carrier relationships
- More efficient operations
- Higher service commands premium
- Specialize where possible
- Negotiate better shipper rates

**When to Walk Away:**
- Below floor margin
- High problem potential
- Bad payment history
- Not worth the stress`
          }
        ]
      },
      {
        id: "broker-m6",
        title: "Load Coordination",
        description: "Matching freight and managing timelines",
        lessons: [
          {
            id: "broker-m6-l1",
            title: "Matching Freight",
            duration: "35 min",
            content: `Efficient load matching is core to brokerage operations.

**Load Information Needed:**

*From Shipper:*
- Pickup location and date/time
- Delivery location and date/time
- Commodity description
- Weight and dimensions
- Special requirements
- Rate agreed

*For Carrier:*
- Clear pickup details
- Clear delivery details
- Rate offered
- Special requirements
- Contact information

**Finding Carriers:**

*Posted Loads:*
- Load boards (DAT, Truckstop)
- Clear descriptions
- Reasonable rates
- Quick response

*Carrier Network:*
- Contact known carriers first
- Check availability
- Better service typically
- Relationship advantage

**Matching Criteria:**

*Equipment:*
- Right type (van, reefer, flatbed)
- Right size
- Special features if needed

*Availability:*
- Right location
- Right timing
- Capacity confirmed

*Qualifications:*
- Authority valid
- Insurance current
- Safety acceptable
- Experience appropriate

**Selection Process:**
1. Identify requirements
2. Search available carriers
3. Verify qualifications
4. Negotiate rate
5. Confirm booking
6. Send documentation`
          },
          {
            id: "broker-m6-l2",
            title: "Managing Timelines",
            duration: "30 min",
            content: `Timeline management keeps shipments on track.

**Critical Milestones:**

*Pre-Pickup:*
- Load confirmed with shipper
- Carrier assigned and confirmed
- Rate confirmation sent and signed
- Driver contact provided
- Pickup appointment set

*Pickup:*
- Carrier arrives on time
- Load matches description
- BOL obtained
- Pickup confirmed to shipper

*Transit:*
- Check calls completed
- ETA updates provided
- Problems addressed immediately
- Delivery appointment confirmed

*Delivery:*
- On-time arrival
- Load accepted
- POD obtained
- Shipper notified
- Any issues documented

**Communication Cadence:**

*To Shipper:*
- Booking confirmation
- Pickup confirmation
- Transit update (long hauls)
- Delivery confirmation

*To Carrier:*
- Rate confirmation
- Any changes immediately
- Support as needed

**Handling Delays:**
1. Know as early as possible
2. Notify shipper immediately
3. Provide new timeline
4. Keep updating
5. Document everything

**Tools for Tracking:**
- TMS software
- ELD integration
- GPS tracking
- Manual check calls
- Automated alerts`
          }
        ]
      },
      {
        id: "broker-m7",
        title: "Broker Workflow System",
        description: "Step-by-step process and load lifecycle",
        lessons: [
          {
            id: "broker-m7-l1",
            title: "Step-by-Step Process",
            duration: "35 min",
            content: `A systematic workflow ensures consistent execution.

**Phase 1: Lead Generation**
- Prospecting activities
- Inbound inquiries
- Referrals
- Lead qualification

**Phase 2: Quote & Booking**
- Receive shipment details
- Research carrier costs
- Calculate margin
- Provide shipper quote
- Negotiate and close
- Send shipper confirmation

**Phase 3: Carrier Assignment**
- Post load/contact carriers
- Negotiate carrier rate
- Verify carrier qualifications
- Book carrier
- Send rate confirmation
- Confirm acceptance

**Phase 4: Execution**
- Monitor pickup
- Confirm loaded
- Track transit
- Conduct check calls
- Handle issues
- Monitor delivery
- Collect POD

**Phase 5: Settlement**
- Invoice shipper
- Process carrier payment
- Resolve any disputes
- Close file

**Phase 6: Follow-Up**
- Quality check
- Request feedback
- Ask for more business
- Maintain relationship

**Documentation at Each Phase:**
- Quote records
- Rate confirmations
- BOLs
- PODs
- Invoices
- Communications`
          },
          {
            id: "broker-m7-l2",
            title: "Load Lifecycle",
            duration: "30 min",
            content: `Understanding the complete load lifecycle.

**Stage 1: Opportunity**
- Shipper has freight need
- You're contacted or reach out
- Initial information gathered
- Potential assessed

**Stage 2: Quote**
- Complete details obtained
- Carrier costs researched
- Margin calculated
- Quote provided
- Negotiation if needed

**Stage 3: Booked**
- Shipper accepts quote
- Confirmation sent
- Carrier needed
- Timeline set

**Stage 4: Covered**
- Carrier found
- Rate negotiated
- Carrier verified
- Rate confirmation signed
- All parties informed

**Stage 5: In Transit**
- Pickup confirmed
- BOL obtained
- Tracking active
- Check calls made
- Updates provided

**Stage 6: Delivered**
- Delivery confirmed
- POD collected
- Any issues noted
- Shipper informed

**Stage 7: Invoiced**
- Shipper invoiced
- Supporting docs attached
- Payment terms active

**Stage 8: Paid/Closed**
- Shipper payment received
- Carrier payment processed
- File complete
- Records archived

**Tracking Status:**
- Every load should have status
- Update at each milestone
- Easy view of all loads
- Nothing falls through cracks`
          }
        ]
      },
      {
        id: "broker-m8",
        title: "Communication & Follow-Up",
        description: "Maintaining relationships and repeat business",
        lessons: [
          {
            id: "broker-m8-l1",
            title: "Maintaining Relationships",
            duration: "30 min",
            content: `Long-term relationships drive sustainable business.

**Shipper Relationships:**

*Regular Communication:*
- Not just when you need something
- Industry updates
- Market information
- Checking in periodically

*Service Quality:*
- Deliver as promised
- Solve problems quickly
- Be proactive
- Exceed expectations

*Growing Business:*
- Ask about other lanes
- Other departments/divisions
- Referrals to other shippers
- Expand services offered

**Carrier Relationships:**

*Fair Treatment:*
- Consistent rates
- Quick payment
- Honest communication
- Respect their business

*Value Adding:*
- Consistent freight
- Good information
- Problem support
- Relationship priority

**Key Touchpoints:**

*Regular:*
- Post-shipment follow-up
- Monthly check-in
- Quarterly review
- Annual relationship review

*As Needed:*
- Market updates
- Issue resolution
- Opportunity sharing
- Appreciation expression

**Relationship Health Indicators:**
- Regular business flow
- Quick response to contact
- Referral willingness
- Honest feedback given
- Problem tolerance`
          },
          {
            id: "broker-m8-l2",
            title: "Repeat Business Strategy",
            duration: "30 min",
            content: `Repeat business is more profitable than new business.

**Why Repeat Business Matters:**
- Lower acquisition cost
- Higher margins often possible
- Easier operations
- Predictable revenue
- Reference value

**Building Repeat Business:**

*Consistent Service:*
- Same quality every time
- Reliable execution
- Predictable experience
- Professional always

*Relationship Investment:*
- Remember details
- Anticipate needs
- Provide extra value
- Be easy to work with

*Active Requesting:*
- Ask for more business
- Inquire about other needs
- Request referrals
- Propose new services

**After Each Shipment:**
1. Confirm successful delivery
2. Thank them for business
3. Ask about experience
4. Request feedback
5. Ask about next shipment
6. Schedule follow-up

**Quarterly Activities:**
- Review relationship health
- Analyze volume and margin
- Identify growth opportunities
- Plan relationship investments
- Address any issues

**Red Flags:**
- Declining volume
- Communication changes
- Negative feedback
- Competitor mentions
- Payment changes

**Recovery:**
- Address issues immediately
- Communicate openly
- Solve problems
- Re-earn trust
- Don't give up easily`
          }
        ]
      }
    ]
  }
];

export const BUNDLE_INFO = {
  name: "Complete Academy Bundle",
  description: "Access all 4 courses at a significant discount. Perfect for carriers who want comprehensive education.",
  price: 800,
  savings: 200,
  courses: COURSES.map(c => c.id)
};

export const HOW_IT_WORKS = [
  {
    step: 1,
    title: "Purchase Access",
    description: "Choose individual courses or the complete bundle for maximum savings."
  },
  {
    step: 2,
    title: "Receive Your Code",
    description: "Get your unique QBA access code instantly after purchase."
  },
  {
    step: 3,
    title: "Enter The Academy",
    description: "Use your code in The Hive to unlock your courses."
  },
  {
    step: 4,
    title: "Learn & Progress",
    description: "Complete lessons at your own pace with progress tracking."
  }
];

export const TESTIMONIALS = [
  {
    name: "Marcus Williams",
    role: "Owner-Operator",
    content: "CDL Mastery helped me pass my test on the first try. The pre-trip section alone is worth the investment.",
    course: "cdl-mastery-101"
  },
  {
    name: "Sarah Chen",
    role: "Independent Dispatcher",
    content: "Dispatcher 101 transformed my approach to rate negotiation. I've increased my carriers' revenue by 20%.",
    course: "dispatcher-101"
  },
  {
    name: "David Thompson",
    role: "Fleet Owner",
    content: "Trucking 101 gave me the business foundation I was missing. Finally understand my numbers.",
    course: "trucking-101"
  }
];

export const FAQ = [
  {
    question: "Do I need a Hive subscription to access the Academy?",
    answer: "Yes, Queen B Academy is an add-on to The Hive. You'll need an active Hive account (including the 14-day trial) to access your purchased courses."
  },
  {
    question: "How long do I have access to my purchased courses?",
    answer: "Once purchased, you have lifetime access to your courses. Learn at your own pace, review materials anytime."
  },
  {
    question: "Can I purchase individual courses instead of the bundle?",
    answer: "Absolutely. Each course is available for $250. However, the bundle saves you $200 and gives you access to all four courses."
  },
  {
    question: "What format is the course content?",
    answer: "Courses include detailed written lessons, practical exercises, and real-world examples. All content is accessible directly in The Hive dashboard."
  },
  {
    question: "Is there a refund policy?",
    answer: "We offer a 7-day satisfaction guarantee. If you're not satisfied with your purchase, contact us within 7 days for a full refund."
  },
  {
    question: "How do access codes work?",
    answer: "After purchase, you'll receive a unique QBA access code. Enter this code in The Hive to unlock your courses instantly."
  }
];

export default {
  ACADEMY_INFO,
  COURSES,
  BUNDLE_INFO,
  HOW_IT_WORKS,
  TESTIMONIALS,
  FAQ
};
